import {type IconName, TagGroup} from '@wandb/components';
import React from 'react';

import {urlPrefixed} from '../../../config';
import * as Urls from '../../../util/urls';
import {Tooltip} from '../../../weave/components/Tooltip';
import type {ComposerReferencePart} from '../composer/slate';
import {useNavigateToURL} from '../navigateToURL';

export interface ReferenceTagProps {
  icon: IconName;
  text: string;
  tooltip?: {children: React.ReactNode};
  onRemove?: () => void;
  onOpen?: () => void;
}

/** Shared inline tag for structured references rendered in user messages. */
export const ReferenceTag = ({
  icon,
  text,
  tooltip,
  onRemove,
  onOpen,
}: ReferenceTagProps) => {
  const tag = (
    <span className="mr-4 inline-flex max-w-full align-middle [&_[role=grid]]:min-w-0 [&_[role=grid]]:w-auto [&_[role=row]]:![border-radius:var(--border-radius-sm)]">
      <TagGroup
        aria-label={text}
        color="blue"
        wrap={false}
        items={[
          {
            id: 'reference',
            icon,
            textValue: text,
            cssTruncate: true,
            removable: onRemove != null,
          },
        ]}
        onRemove={onRemove == null ? undefined : onRemove}
        onAction={onOpen}
      />
    </span>
  );

  return tooltip == null ? (
    tag
  ) : (
    <Tooltip content={tooltip.children} noTriggerWrap trigger={tag} />
  );
};

export const ComposerReferenceTag = ({
  part,
  onRemove,
}: {
  part: ComposerReferencePart;
  onRemove?: () => void;
}) => {
  const {icon, text} = referencePresentation(part);
  return <ReferenceTag icon={icon} text={text} onRemove={onRemove} />;
};

/**
 * A reference in a sent message. Clicking it opens the page of the object it
 * references, when that object has one.
 */
export const MessageReferenceTag = ({part}: {part: ComposerReferencePart}) => {
  const navigateToURL = useNavigateToURL();
  const {icon, text} = referencePresentation(part);
  const path = referencePath(part);
  return (
    <ReferenceTag
      icon={icon}
      text={text}
      onOpen={
        path == null
          ? undefined
          : () => navigateToURL(new URL(urlPrefixed(path)))
      }
    />
  );
};

/** The app path of the referenced object's page, if it has one. */
function referencePath(part: ComposerReferencePart): string | undefined {
  switch (part.type) {
    case 'wandb-run-file':
      return Urls.runTabFile(
        part.entityName,
        part.projectName,
        part.runName,
        part.filePath.split('/')
      );
    case 'wandb-run':
      return Urls.run({
        entityName: part.entityName,
        projectName: part.projectName,
        name: part.runName,
      });
    case 'wandb-call':
      return Urls.weaveView({
        entityName: part.entityName,
        projectName: part.projectName,
        weaveTab: 'calls',
        itemName: part.callId,
      });
    case 'wandb-workspace-panel':
      return undefined;
  }
}

function referencePresentation(part: ComposerReferencePart): {
  icon: IconName;
  text: string;
} {
  switch (part.type) {
    case 'wandb-run-file':
      return {icon: 'document', text: part.filePath};
    case 'wandb-run':
      return {
        icon: 'running-repeat',
        text: part.runDisplayName ?? part.runName,
      };
    case 'wandb-workspace-panel':
      return {
        icon: 'panel',
        text: part.queryPanelTableKey ?? part.title ?? part.panelType,
      };
    case 'wandb-call':
      return {
        icon: part.callDisplayName?.toLowerCase().startsWith('eval')
          ? 'baseline-alt'
          : 'layout-tabs',
        text: part.callDisplayName ?? `Call ${part.callId.slice(0, 8)}`,
      };
  }
}
